import { useState, useEffect } from 'react';
import adminService from '../../services/admin';
import toast from 'react-hot-toast';

const PROVIDER_TYPES = [
  { value: 'google_cloud_tts', label: 'Google Cloud TTS' },
  { value: 'amazon_polly', label: 'Amazon Polly' },
  { value: 'azure_tts', label: 'Azure TTS' },
  { value: 'elevenlabs', label: 'ElevenLabs' },
  { value: 'openai_tts', label: 'OpenAI TTS' },
  { value: 'openai_chat', label: 'OpenAI Chat' },
  { value: 'coqui_tts', label: 'Coqui TTS (Local)' },
  { value: 'custom', label: 'Custom' },
];

function APIProviderManagement() {
  const [providers, setProviders] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => {
    loadData();
  }, [includeInactive]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [providersData, usageData] = await Promise.all([
        adminService.getAPIProviders(includeInactive),
        adminService.getAPIUsageSummary(30),
      ]);
      setProviders(providersData.providers);
      setUsageSummary(usageData);
    } catch (error) {
      toast.error('Failed to load API providers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingProvider(null);
    setShowModal(true);
  };

  const handleEdit = (provider) => {
    setEditingProvider(provider);
    setShowModal(true);
  };

  const handleDelete = async (providerId) => {
    if (!confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      await adminService.deleteAPIProvider(providerId);
      toast.success('Provider deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete provider');
    }
  };

  const handleSetDefault = async (providerId) => {
    try {
      await adminService.setDefaultAPIProvider(providerId);
      toast.success('Default provider updated');
      loadData();
    } catch (error) {
      toast.error('Failed to set default provider');
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingProvider) {
        await adminService.updateAPIProvider(editingProvider.id, data);
        toast.success('Provider updated');
      } else {
        await adminService.createAPIProvider(data);
        toast.success('Provider created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save provider');
    }
  };

  const handleSeedProviders = async () => {
    try {
      const result = await adminService.seedAPIProviders();
      toast.success(result.message);
      loadData();
    } catch (error) {
      toast.error('Failed to seed providers');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="spinner w-12 h-12"></span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">API Providers</h1>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-400">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show inactive
          </label>
          {providers.length === 0 && (
            <button onClick={handleSeedProviders} className="btn btn-secondary">
              Seed Default Providers
            </button>
          )}
          <button onClick={handleCreate} className="btn btn-primary">
            + Add Provider
          </button>
        </div>
      </div>

      {/* Usage Summary */}
      {usageSummary && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Total Characters</p>
            <p className="text-2xl font-bold">
              {usageSummary.total_characters?.toLocaleString() || 0}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Total Requests</p>
            <p className="text-2xl font-bold">
              {usageSummary.total_requests?.toLocaleString() || 0}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Audio Minutes</p>
            <p className="text-2xl font-bold">
              {usageSummary.total_audio_minutes?.toFixed(1) || 0}
            </p>
          </div>
          <div className="card text-center">
            <p className="text-gray-400 text-sm mb-1">Total Cost</p>
            <p className="text-2xl font-bold text-green-400">
              ${usageSummary.total_cost?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      )}

      {/* Providers List */}
      <div className="space-y-4">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className={`card ${!provider.is_active ? 'opacity-60' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold">{provider.name}</h3>
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">
                    {PROVIDER_TYPES.find((t) => t.value === provider.provider_type)?.label ||
                      provider.provider_type}
                  </span>
                  {provider.is_default && (
                    <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">
                      Default
                    </span>
                  )}
                  {provider.has_api_key && (
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">
                      API Key Set
                    </span>
                  )}
                  <span
                    className={`px-2 py-0.5 text-xs rounded ${
                      provider.health_status === 'healthy'
                        ? 'bg-green-500/20 text-green-400'
                        : provider.health_status === 'unhealthy'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}
                  >
                    {provider.health_status}
                  </span>
                </div>
                
                {provider.description && (
                  <p className="text-gray-400 text-sm mb-2">{provider.description}</p>
                )}

                <div className="flex gap-6 text-sm text-gray-400">
                  <span>Per Char: ${provider.cost_per_character}</span>
                  <span>Per Request: ${provider.cost_per_request}</span>
                  <span>Per Minute: ${provider.cost_per_minute}</span>
                  <span>Rate: {provider.rate_limit_per_minute}/min</span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(provider)}
                  className="btn btn-secondary btn-sm"
                >
                  Edit
                </button>
                {!provider.is_default && (
                  <>
                    <button
                      onClick={() => handleSetDefault(provider.id)}
                      className="btn btn-secondary btn-sm"
                    >
                      Set Default
                    </button>
                    <button
                      onClick={() => handleDelete(provider.id)}
                      className="text-red-400 hover:text-red-300 px-2"
                    >
                      🗑
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {providers.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No API providers configured. Click "Seed Default Providers" to get started.
        </div>
      )}

      {showModal && (
        <ProviderModal
          provider={editingProvider}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

function ProviderModal({ provider, onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: provider?.name || '',
    provider_type: provider?.provider_type || 'coqui_tts',
    description: provider?.description || '',
    api_key: '',
    api_secret: '',
    base_url: provider?.base_url || '',
    cost_per_character: provider?.cost_per_character || 0,
    cost_per_request: provider?.cost_per_request || 0,
    cost_per_minute: provider?.cost_per_minute || 0,
    rate_limit_per_minute: provider?.rate_limit_per_minute || 60,
    rate_limit_per_day: provider?.rate_limit_per_day || 10000,
    is_active: provider?.is_active ?? true,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = { ...formData };
    // Only include API key/secret if they're set
    if (!data.api_key) delete data.api_key;
    if (!data.api_secret) delete data.api_secret;
    
    onSave(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-auto py-8">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-auto">
        <h2 className="text-xl font-semibold mb-4">
          {provider ? 'Edit Provider' : 'Add Provider'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Provider Type *</label>
              <select
                value={formData.provider_type}
                onChange={(e) =>
                  setFormData({ ...formData, provider_type: e.target.value })
                }
                className="input w-full"
                disabled={!!provider}
              >
                {PROVIDER_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input w-full"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Base URL</label>
            <input
              type="text"
              value={formData.base_url}
              onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
              className="input w-full"
              placeholder="https://api.example.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">
                API Key {provider?.has_api_key && '(leave blank to keep existing)'}
              </label>
              <input
                type="password"
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                className="input w-full"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">API Secret</label>
              <input
                type="password"
                value={formData.api_secret}
                onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
                className="input w-full"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Cost per Character</label>
              <input
                type="number"
                step="0.000001"
                value={formData.cost_per_character}
                onChange={(e) =>
                  setFormData({ ...formData, cost_per_character: parseFloat(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Cost per Request</label>
              <input
                type="number"
                step="0.0001"
                value={formData.cost_per_request}
                onChange={(e) =>
                  setFormData({ ...formData, cost_per_request: parseFloat(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Cost per Minute</label>
              <input
                type="number"
                step="0.0001"
                value={formData.cost_per_minute}
                onChange={(e) =>
                  setFormData({ ...formData, cost_per_minute: parseFloat(e.target.value) })
                }
                className="input w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Rate Limit (per minute)</label>
              <input
                type="number"
                value={formData.rate_limit_per_minute}
                onChange={(e) =>
                  setFormData({ ...formData, rate_limit_per_minute: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Rate Limit (per day)</label>
              <input
                type="number"
                value={formData.rate_limit_per_day}
                onChange={(e) =>
                  setFormData({ ...formData, rate_limit_per_day: parseInt(e.target.value) })
                }
                className="input w-full"
              />
            </div>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
            <span className="text-sm">Active</span>
          </label>

          <div className="flex gap-2 pt-4">
            <button type="submit" className="btn btn-primary flex-1">
              {provider ? 'Update' : 'Create'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default APIProviderManagement;
